import { apiBaseUrl } from "./api";
import { sessionExpired } from "./session";
import { OfferApiError } from "./offerApi";

// Présentations client : mêmes conventions que offerApi (statut HTTP et détail gardés dans l'erreur).
const authHeaders = () => {
  const token = localStorage.getItem("auth_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const fail = async (response, fallback) => {
  if (response.status === 401) {
    sessionExpired();
    throw new OfferApiError(401, "Session expirée. Veuillez vous reconnecter.");
  }
  const data = await response.json().catch(() => null);
  let detail = data?.detail ?? fallback;
  if (Array.isArray(detail)) detail = detail.map((entry) => entry?.msg || JSON.stringify(entry)).join(" ; ");
  throw new OfferApiError(response.status, detail);
};

const request = async (endpoint, { method = "GET", body } = {}) => {
  const response = await fetch(`${apiBaseUrl}${endpoint}`, {
    method,
    headers: { "Content-Type": "application/json", ...authHeaders() },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  if (!response.ok) await fail(response, "Une erreur est survenue");
  return response.json().catch(() => null);
};

const query = (params) => {
  const search = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") search.set(key, value);
  });
  const text = search.toString();
  return text ? `?${text}` : "";
};

// PDF imprimé par le serveur : { blob, filename }.
const downloadPdf = (base, fallback) => async (id, revisionId) => {
  const response = await fetch(`${apiBaseUrl}${base}/${id}/pdf${query({ revision_id: revisionId })}`, {
    method: "POST", headers: authHeaders(),
  });
  if (!response.ok) await fail(response, "Le PDF n'a pas pu être préparé.");
  const disposition = response.headers.get("Content-Disposition") || "";
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  return { blob: await response.blob(), filename: encoded ? decodeURIComponent(encoded[1]) : fallback };
};

// Documents des jumeaux : lus une fois (contenu immuable), partagés par toutes les slides.
const documents = new Map();
const embedDocument = (embedId) => {
  if (!documents.has(embedId)) {
    documents.set(embedId, fetch(`${apiBaseUrl}/presentations/embeds/${embedId}/document`, { headers: authHeaders() })
      .then((response) => (response.ok ? response.text() : Promise.reject(new OfferApiError(response.status, "Jumeau indisponible"))))
      .catch((error) => { documents.delete(embedId); throw error; }));
  }
  return documents.get(embedId);
};

// Présentations et rapports partagent leurs tables côté serveur : les routes d'un document sont sous /presentations
// ou /reports, les routes communes (jumeaux, rattachements, consignes, commentaires, Drive) sous /presentations.
const common = {
  scopes: (q) => request(`/presentations/scopes${query({ q })}`),
  scope: (type, id) => request(`/presentations/scope/${type}/${id}`),
  resolveComment: (commentId, reply) =>
    request(`/presentations/comments/${commentId}/resolve`, { method: "POST", body: { reply } }),
  reopenComment: (commentId) => request(`/presentations/comments/${commentId}/reopen`, { method: "POST" }),
  deleteComment: (commentId) => request(`/presentations/comments/${commentId}`, { method: "DELETE" }),
  retireRule: (ruleId, reason) =>
    request(`/presentations/guides/rules/${ruleId}/retire`, { method: "POST", body: { reason } }),
  driveConnect: (returnTo) => request("/presentations/drive/connect", { method: "POST", body: { return_to: returnTo } }),
  embeds: (params) => request(`/presentations/embeds${query(params)}`),
  embed: (id) => request(`/presentations/embeds/${id}`),
  uploadEmbed: (body) => request("/presentations/embeds", { method: "POST", body }),
  embedFromFocus: (body) => request("/presentations/embeds/from-focus", { method: "POST", body }),
  refreshPoster: (id) => request(`/presentations/embeds/${id}/poster`, { method: "POST" }),
  embedDocument,
};

const documentApi = (base, doc, fallbackName) => ({
  ...common,
  doc,
  list: (params) => request(`${base}${query(params)}`),
  create: (body) => request(base, { method: "POST", body }),
  get: (id, revisionId) => request(`${base}/${id}${query({ revision_id: revisionId })}`),
  save: (id, body) => request(`${base}/${id}`, { method: "PUT", body }),
  updateMeta: (id, body) => request(`${base}/${id}`, { method: "PATCH", body }),
  revisions: (id) => request(`${base}/${id}/revisions`),
  restore: (id, revisionId, baseToken) =>
    request(`${base}/${id}/revisions/${revisionId}/restore`, { method: "POST", body: { base_token: baseToken } }),
  label: (id, revisionId, label) => request(`${base}/${id}/revisions/${revisionId}/label`, { method: "PUT", body: { label } }),
  pdf: downloadPdf(base, fallbackName),
  saveTemplate: (id, level, clear = false) => request(`${base}/${id}/template`, { method: "POST", body: { level, clear } }),
  comments: (id, status) => request(`${base}/${id}/comments${query({ status })}`),
  addComment: (id, body) => request(`${base}/${id}/comments`, { method: "POST", body }),
  guides: (scopeType, scopeId) => request(`/presentations/guides/${scopeType}/${scopeId}${query({ doc })}`),
  addGuideNote: (id, body, level) =>
    request(`${base}/${id}/guide-notes`, { method: "POST", body: { body, ...(level ? { level } : {}) } }),
  keepClientLogo: (id, logo) => request(`${base}/${id}/client-logo`, { method: "POST", body: { logo } }),
  driveStatus: (id) => request(`${base}/${id}/drive`),
  driveSync: (id) => request(`${base}/${id}/drive/sync`, { method: "POST" }),
});

// Points client : les présentations de plusieurs projets d'un client réunies sans être recopiées.
const pointPdf = downloadPdf("/presentations/points", "Point.pdf");
export const pointApi = {
  list: (params) => request(`/presentations/points${query(params)}`),
  candidates: (scopeType, scopeId) => request(`/presentations/points/candidates/${scopeType}/${scopeId}`),
  create: (body) => request("/presentations/points", { method: "POST", body }),
  get: (id) => request(`/presentations/points/${id}`),
  update: (id, body) => request(`/presentations/points/${id}`, { method: "PATCH", body }),
  pdf: (id) => pointPdf(id),
};

export const presentationApi = documentApi("/presentations", "presentation", "Presentation.pdf");
export const reportApi = documentApi("/reports", "report", "Rapport.pdf");
